import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ScrollableAnchor from 'react-scrollable-anchor';
import {
  COMMENTTYPE_HEARING_RESPONSE,
  COMMENTTYPE_HEARING_REVIEW,
  CONTENTTYPE_FILE,
  CONTENTTYPE_TEXT,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_CREATED,
  HEARINGSTATUS_DRAFT,
} from '../../../../constants/api';
import { currentUserOwnsHearingByIdSelector } from '../../../../features/apiFeatures/hearingSlice';
import { currentUserIdSelector } from '../../../../features/authenticationSlice';
import {
  commentDataSelector,
  commentModelSelector,
  resetContentDiff,
  updateContentDiff,
  createOrUpdateCommentContentInitialize,
  isFetchingCommentsSelector,
  isUpdatingCommentSelector,
  isResponsePanelOpenSelector,
  setIsResponsePanelOpen,
  openCommentIdSelector,
  setOpenCommentId,
  deleteCommentInitialize,
  onBehalfOfSelector,
  isOnBehalfOfSelector,
  setIsOnBehalfOf,
  setOnBehalfOf,
  currentPageCommentsSelector,
  totalPagesCommentsSelector,
  areCommentsReadyForPageSelector,
  paginatedCommentsSelector,
  updateCurrentCommentsPage,
  totalCommentsSelector,
} from '../../../../features/componentFeatures/commentSectionSlice';
import { getAttr, getEntityId } from '../../../../features/helpers/apiHelpers';
import { mapFileContentToFileItems } from '../../../../utilities/mapperFunctions';
import { Comment } from './commentSection/Comment';
import { CommentSectionHeader } from './commentSection/CommentSectionHeader';
import { EditCommentPanel } from './commentSection/EditCommentPanel';
import { paginateItems } from '../pagination/paginateItems';
import { Pagination } from '../pagination/Pagination';
import { shouldPaginateCommentsSelector } from '../../../../features/bootstrapSlice';

const CommentSection = ({ hearing, hearingStatus }) => {
  const { t: translator } = useTranslation();
  const dispatch = useDispatch();

  const onChange = (
    newValue,
    commentId,
    contentType,
    allowMultiple,
    isDeleteOperation,
  ) => {
    dispatch(
      updateContentDiff({
        contentType,
        content: newValue,
        commentId,
        allowMultiple,
        isDeleteOperation,
      }),
    );
  };

  const hearingId = getEntityId(hearing);

  const usingPaginatation = useSelector(shouldPaginateCommentsSelector);
  const currentPage = useSelector(currentPageCommentsSelector);
  const totalPages = useSelector(totalPagesCommentsSelector);
  const commentsReady = useSelector(areCommentsReadyForPageSelector);
  const commentIdsForPage = useSelector(paginatedCommentsSelector);
  const numberOfComments = useSelector(totalCommentsSelector);

  const commentType =
    hearingStatus === HEARINGSTATUS_DRAFT ||
    hearingStatus === HEARINGSTATUS_CREATED
      ? COMMENTTYPE_HEARING_REVIEW
      : COMMENTTYPE_HEARING_RESPONSE;

  const comments = useSelector(commentDataSelector(hearingId, commentType));
  const isFetchingComments = useSelector(isFetchingCommentsSelector);
  const isUpdatingComment = useSelector(isUpdatingCommentSelector);
  const isResponsePanelOpen = useSelector(isResponsePanelOpenSelector);
  const openCommentId = useSelector(openCommentIdSelector);
  const currentUserId = useSelector(currentUserIdSelector);
  const isCurrentUserOwnerOfHearing = useSelector(
    currentUserOwnsHearingByIdSelector(hearingId),
  );

  const openComment = useSelector(
    commentModelSelector(hearingId, openCommentId),
  );
  const onBehalfOf = useSelector(onBehalfOfSelector);
  const isOnBehalfOf = useSelector(isOnBehalfOfSelector);

  const openCommentText = openComment && openComment.textContent;
  const openCommentFiles = openComment ? openComment.fileContent : [];
  const textToRender = getAttr(openCommentText, 'textContent');
  const filesToRender = openCommentFiles.map(mapFileContentToFileItems);

  const onChangeText = (value) =>
    onChange(value, openCommentId, CONTENTTYPE_TEXT);
  const onChangeFiles = (value, isDeleteOperation) =>
    onChange(value, openCommentId, CONTENTTYPE_FILE, true, isDeleteOperation);
  const onPanelDismiss = () => {
    dispatch(setIsResponsePanelOpen(false));
    dispatch(setOpenCommentId(null));
    dispatch(resetContentDiff());
  };
  const onCreateNewComment = () => dispatch(setIsResponsePanelOpen(true));
  const onUpdateComment = (commentId) => {
    dispatch(setOpenCommentId(commentId));
    dispatch(setIsResponsePanelOpen(true));
    dispatch(resetContentDiff());
  };
  const onCreateOrUpdateComment = () =>
    dispatch(
      createOrUpdateCommentContentInitialize({
        hearingId: hearingId,
        commentId: openComment.id,
        hearingStatus,
        isCurrentUserOwnerOfHearing,
      }),
    );

  const onDeleteComment = (commentId) =>
    dispatch(deleteCommentInitialize({ hearingId, commentId }));

  const onChangePage = (value) =>
    dispatch(
      updateCurrentCommentsPage({ pageIndex: value, hearingId: hearingId }),
    );

  const isHearingCreatedOrDraft =
    hearingStatus === HEARINGSTATUS_DRAFT ||
    hearingStatus === HEARINGSTATUS_CREATED;

  const isHearingAwaitingConclusion =
    hearingStatus === HEARINGSTATUS_AWAITING_CONCLUSION;

  const disableCreateCommentButton =
    hearingStatus !== HEARINGSTATUS_DRAFT &&
    hearingStatus !== HEARINGSTATUS_ACTIVE &&
    !(isHearingAwaitingConclusion && isCurrentUserOwnerOfHearing);

  const disableOnBehalfOf =
    hearingStatus !== HEARINGSTATUS_ACTIVE &&
    !(isHearingAwaitingConclusion && isCurrentUserOwnerOfHearing);

  const isCommentsEditable =
    hearingStatus === HEARINGSTATUS_DRAFT ||
    hearingStatus === HEARINGSTATUS_ACTIVE;

  const showCommentFooter =
    commentType === COMMENTTYPE_HEARING_RESPONSE && isCurrentUserOwnerOfHearing;

  const showSpinner = isFetchingComments && !commentsReady;

  const commentSectionHeader = isHearingCreatedOrDraft
    ? translator('pages.hearingDetails.commentComments')
    : translator('pages.hearingDetails.commentReplies');

  const createCommentText = isHearingCreatedOrDraft
    ? translator('pages.hearingDetails.hearingCreateComment')
    : translator('pages.hearingDetails.hearingCreateReply');

  const commentsList = usingPaginatation
    ? paginateItems(comments, commentIdsForPage)
    : comments;

  const commentsToRender = commentsList.map((comment) => (
    <Comment
      key={comment.id}
      commentContent={comment}
      isCommentOwner={
        currentUserId &&
        getEntityId(comment.commentOwner) === currentUserId.toString()
      }
      isEditable={isCommentsEditable}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
      showFooter={showCommentFooter}
      hearingDeadline={getAttr(hearing, 'deadline')}
    />
  ));

  const onChangeIsOnBehalfOf = useCallback(
    (value) => dispatch(setIsOnBehalfOf(value)),
    [dispatch],
  );
  const onChangeOnBehalfOf = useCallback(
    (value) => dispatch(setOnBehalfOf(value)),
    [dispatch],
  );

  return (
    <div className="hearing-response">
      <ScrollableAnchor id="commentSection">
        <div>
          <CommentSectionHeader
            headerText={commentSectionHeader}
            numberOfComments={numberOfComments}
            onCreateCommentClick={onCreateNewComment}
            createCommentText={createCommentText}
            disableCreateCommentButton={disableCreateCommentButton}
          />

          {showSpinner ? (
            <Spinner size={SpinnerSize.large} />
          ) : (
            commentsToRender
          )}
          <EditCommentPanel
            commentModel={openComment}
            isOpen={isResponsePanelOpen}
            onDismiss={onPanelDismiss}
            commentText={textToRender}
            commentType={commentType}
            onChangeText={onChangeText}
            onChangeFiles={onChangeFiles}
            commentFiles={filesToRender}
            onCreateOrUpdateComment={onCreateOrUpdateComment}
            showSpinner={isUpdatingComment}
            isEditMode={openCommentId}
            onBehalfOf={onBehalfOf}
            isOnBehalfOf={isOnBehalfOf}
            onChangeIsOnBehalfOf={onChangeIsOnBehalfOf}
            onChangeOnBehalfOf={onChangeOnBehalfOf}
            disableOnBehalfOf={disableOnBehalfOf}
            isHearingCreatedOrDraft={isHearingCreatedOrDraft}
            isHearingAwaitingConclusion={isHearingAwaitingConclusion}
            isCurrentUserOwnerOfHearing={isCurrentUserOwnerOfHearing}
          />
          {usingPaginatation ? (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChangePage={onChangePage}
            />
          ) : null}
        </div>
      </ScrollableAnchor>
    </div>
  );
};

export { CommentSection };
